import {formatUnits} from 'ethers';
import type {Asset, DisplayUsdQuote, Launch, LiquidityIncentives, SourceAssetPrice} from '../../../../packages/sdk/src';
import {useData} from './data';
import {companyForAsset} from './assetCatalog';

export function freshUsdRate(quote: DisplayUsdQuote | null | undefined, symbol: string, now = Date.now()) {
  const rate = Number(quote?.usd);
  return quote?.quoteSymbol === symbol && Number.isFinite(rate) && rate > 0 && Number.isFinite(quote.asOf) && quote.asOf <= now + 30000 && Number.isFinite(quote.validUntil) && quote.validUntil > now ? rate : null;
}

export function sourceUsdQuote(asset: Asset | undefined, symbol: string, quotes: SourceAssetPrice[], now = Date.now()): DisplayUsdQuote | null {
  if (!asset || asset.symbol !== symbol) return null;
  const fixture = asset.status === 'local-fixture';
  if (!fixture && asset.sourceChain.toLowerCase() !== 'solana') return null;
  const company = fixture ? companyForAsset(asset) : undefined;
  const price = quotes.find(q => fixture ? q.name === company?.name : q.sourceMint === asset.sourceMint);
  if (!price) return null;
  const quote = {quoteSymbol: symbol, usd: price.usd, asOf: price.fetchedAt, validUntil: price.validUntil, source: fixture ? 'Test-market estimate using live source-token price' : 'DEX Screener source-token reference', sourceUrl: price.sourceUrl};
  return freshUsdRate(quote, symbol, now) === null ? null : quote;
}

export function useMarketUsd(launch: Launch | null, asset?: Asset) {
  const bnb = useData<{quote: DisplayUsdQuote | null}>(launch && launch.state !== 'live' ? '/prices/bnb-usd' : null, 30000);
  const pool = useData<LiquidityIncentives>(launch?.state === 'live' ? `/launches/${launch.id}/liquidity-incentives` : null, 30000);
  const source = useData<{quotes: SourceAssetPrice[]}>(launch?.state === 'live' && asset ? '/prices/source-assets' : null, 30000);
  const valuation = pool.data;
  if (launch?.state === 'live') {
    if (asset?.status === 'local-fixture') return sourceUsdQuote(asset, launch.priceDenomination, source.data?.quotes ?? []);
    const attested = !pool.error && valuation?.available && valuation.valuationFresh ? {
    quoteSymbol: valuation.quoteSymbol, usd: formatUnits(valuation.receiptPriceUsd, valuation.usdDecimals), asOf: valuation.valuationAt, validUntil: valuation.valuationUntil, source: 'Operator-attested receipt valuation',
  } : null;
    return freshUsdRate(attested, launch.priceDenomination) !== null ? attested : sourceUsdQuote(asset, launch.priceDenomination, source.data?.quotes ?? []);
  }
  return bnb.data?.quote ?? null;
}

export function supplyUnits(raw: string) { const n = Number(formatUnits(raw, 18)); return Number.isFinite(n) && n > 0 ? n : 0; }
export function displayValue(value: number, usd = false) {
  if (!Number.isFinite(value)) return '—';
  return (usd ? '$' : '') + value.toLocaleString('en-US', value >= 1000 ? {notation: 'compact', maximumFractionDigits: 2} : {maximumSignificantDigits: 6});
}

export function curveProgress(launch: Pick<Launch, 'raised' | 'target' | 'contractState' | 'state'>) {
  const raised = BigInt(launch.raised), target = BigInt(launch.target);
  const committed = launch.contractState >= 2 || launch.state === 'live';
  const percent = committed ? 100 : target > 0n ? Math.max(0, Math.min(100, Number(raised * 10000n / target) / 100)) : 0;
  return {percent, committed, remaining: committed || raised >= target ? '0' : (target - raised).toString()};
}
